/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Box, Card, CardContent, CircularProgress, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, styled, tableCellClasses, TableSortLabel } from '@mui/material'
import Pagination from '../../../../components/TablePagination'
import React, { useEffect } from 'react'
import { type ConnectedProps, connect } from 'react-redux'
import { type RootState } from '../../../../store'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { fetchBatteryData } from '../../redux/actionCreator'
import { type batteryState } from '../../redux/slices/batteryDataSlice'
import { type ZBatteryData } from '../../services/batteryService'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import { useAuth } from 'react-oidc-context'
import { parseDateInAOHstandard } from '../../../../customHooks/parseDate'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import { visuallyHidden } from '@mui/utils'

import {
  DesktopDateTimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers'
import _ from 'lodash'
import { getDatePickerInfo } from '../../../../utils/datePickerInfo'
import LanguageStrings from '../../../../i18n/locales'

const style = {
  width: '100%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  padding: 0
}

const defaultDate = {
  end: dayjs(new Date()),
  start: dayjs(new Date()).subtract(7, 'day').clone().hour(0).minute(0).second(0)
}

type Order = 'asc' | 'desc'
interface Sort {
  order: Order
  orderBy: keyof Data
}

interface EnhancedTableProps {
  rowCount: number
  dataList: ZBatteryData[]
  order: Order
  orderBy: keyof Data
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void
}
interface Data {
  kpi_name: string
  kpi_timestamp: string
  kpi_value: string
}
const DatePickerInfoStrings = LanguageStrings().Diagnostics.Info

function EnhancedTableHead (props: EnhancedTableProps): any {
  const { dataList, order, orderBy, onRequestSort } = props

  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy)
    }
  // Get the first available kpi_units value from the data
  const kpiUnit = dataList && dataList.length > 0
    ? dataList.find(item => item.kpi_units && item.kpi_units.trim() !== '')?.kpi_units
    : null

  return (dataList !== null && dataList !== undefined && <>
  <TableHead data-testid='table-header'>
        <TableRow>
          {headCells.map((headCell: HeadCell) => {
            let label = headCell.label
            if (headCell.id === 'kpi_value') {
              label = kpiUnit ? `Kpi Value (${kpiUnit})` : 'Kpi Value'
            }
            return (
              <StyledTableCell
                key={headCell.id}
                align='left'
                padding={headCell.disablePadding ? 'none' : 'normal'}
                sortDirection={orderBy === headCell.id ? order : false}
              >
                <TableSortLabel
                  hideSortIcon={!headCell.sortable}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                  aria-label={headCell.label}
                  data-testid="test-sort"
                >
                  {label}
                  {orderBy === headCell.id
                    ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      )
                    : null}
                </TableSortLabel>
              </StyledTableCell>
            )
          })}
        </TableRow>
  </TableHead>
  </>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: keyof Data
  label: string
  numeric: boolean
  sortable: boolean
}

interface ComponentState {
  page: number
  start: Dayjs
  end: Dayjs
  rowsPerPage: number
  selectedKpi: Option | null
  sort: Sort
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'kpi_name',
    numeric: false,
    disablePadding: false,
    label: 'Kpi Name',
    sortable: false
  },
  {
    id: 'kpi_timestamp',
    numeric: false,
    disablePadding: false,
    label: 'Kpi Timestamp',
    sortable: true
  },
  {
    id: 'kpi_value',
    numeric: false,
    disablePadding: false,
    label: 'Kpi Value',
    sortable: true
  }
]

const initialState = {
  page: 1, // the current page
  start: defaultDate.start, // the start date
  end: defaultDate.end, // the end date
  rowsPerPage: 10, // the number of rows per page
  selectedKpi: {
    value: '',
    label: 'Select an option'
  },
  sort: { order: 'asc' as const, orderBy: 'kpi_timestamp' as const }
}

const DiagnosticTable = (props: PropsFromRedux): JSX.Element => {
  const [batteryDataList, setBatteryDataList] = React.useState<ZBatteryData[]>([])
  const [tableParams, dispatchTableParams] = React.useReducer((currentVal: ComponentState, newVal: Partial<ComponentState>) => ({ ...currentVal, ...newVal }), initialState)
  const [isStartModified, setIsStartModified] = React.useState(false)
  const [isEndModified, setIsEndModified] = React.useState(false)
  const [kpiOptions, setKpiOptions] = React.useState<Option[]>([])
  const auth = useAuth()
  const tenant = auth.user?.profile.customer as string
  let paramAssetId = sessionStorage.getItem('paramAssetId')
  // istanbul ignore next
  if (paramAssetId === null) {
    paramAssetId = ''
  }

  // Create KPI options from the diagnostic KPI list data
  React.useEffect(() => {
    const diagnosticKpiList = (props as any).diagnosticKpiList
    if (diagnosticKpiList?.kpiData?.kpiList) {
      const options: Option[] = diagnosticKpiList.kpiData.kpiList.map((kpi: any) => ({
        value: kpi.id,
        label: kpi.name
      }))
      setKpiOptions(options)
      options.length > 0 ? dispatchTableParams({ selectedKpi: options[0], page: 1 }) : dispatchTableParams({ page: 1 })
    }
  }, [(props as any).diagnosticKpiList])

  const CustomLocalizationProviderWrapper = styled('div')({
    backgroundColor: '#505050',
    border: 'none',
    borderRadius: '4px'
  })

  const WhiteDatePicker = styled(DesktopDateTimePicker)({
    '& input': {
      color: '#D0D0D0',
      fontSize: '14px',
      paddingRight: 0
    }
  })

  // istanbul ignore next
  const handleDateChange = (type: string, date: dayjs.Dayjs): void => {
    const temp = _.clone(tableParams)

    let adjustedDate = date
    if (date.isSame(dayjs(), 'day') && date.isAfter(dayjs())) {
      adjustedDate = dayjs().startOf('day')
    }

    if (type === 'start') {
      setIsStartModified(true)
      temp.start = adjustedDate
      if (!isEndModified) {
        let newEnd = adjustedDate.clone().add(7, 'day')
        if (newEnd.isAfter(dayjs())) {
          newEnd = dayjs()
        }
        temp.end = newEnd
      }
    } else if (type === 'end') {
      setIsEndModified(true)
      temp.end = adjustedDate
      if (!isStartModified) {
        const newStart = adjustedDate.clone().subtract(7, 'day')
        temp.start = newStart
      }
    }

    dispatchTableParams({ ...temp, page: 1 })
  }

  // istanbul ignore next
  const handleKpiChange = (selectedOption: Option): void => {
    // Clear the current data when KPI selection changes
    setBatteryDataList([])
    dispatchTableParams({ selectedKpi: selectedOption, page: 1 })
  }

  const fetchBatteryData = (): any => {
    // istanbul ignore next
    if (paramAssetId !== undefined && tableParams.start !== (undefined) && tableParams.start !== null && tableParams.end !== undefined && tableParams.end !== null && tableParams.start <= tableParams.end) {
      const startMoment = tableParams.start
        .clone()
        .set('second', 0)
      const endMoment = tableParams.end
        .clone()
        .set('second', 59)
      // Use the selected KPI ID from the dropdown
      const kpiId = tableParams.selectedKpi?.value ?? ''
      props.fetchBatteryData(tenant, paramAssetId as string, startMoment.format('YYYY-MM-DDTHH:mm:ssZ'), endMoment.format('YYYY-MM-DDTHH:mm:ssZ'), tableParams.page, tableParams.rowsPerPage, kpiId, tableParams.sort.orderBy, tableParams.sort.order)
    }
  }

  const isInitialMount = React.useRef(true)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    fetchBatteryData()
  }, [tableParams])

  /* istanbul ignore next */
  const handleFetchBatteryDataList = (): void => {
    tableParams.page >= 1 && fetchBatteryData()
  }

  useEffect(() => {
    if (props.batteryData.data !== null && props.batteryData.httpStatus === 200) {
      setBatteryDataList(props.batteryData.data.kpi_values)
    } else if (props.batteryData.isLoading) {
      // Clear the table data when loading starts to prevent showing stale data
      setBatteryDataList([])
    }
  }, [props.batteryData.data?.kpi_values, props.batteryData.isLoading])

  const handleRequestSort =
    (event: React.MouseEvent<unknown>, newOrderBy: keyof Data): void => {
      const isAsc = tableParams.sort.orderBy === newOrderBy && tableParams.sort.order === 'asc'
      const toggledOrder = isAsc ? 'desc' : 'asc'
      dispatchTableParams({ sort: { order: toggledOrder, orderBy: newOrderBy } })
    }
  const handleTable = (): JSX.Element => {
    // istanbul ignore else
    if (props.batteryData.data !== null && props.batteryData.data.kpi_values.length > 0 && props.batteryData.httpStatus === 200) {
      return (
        <>
          <Paper
            sx={{
              width: '100%',
              mb: 2,
              backgroundColor: '#272727',
              boxShadow: 'unset',
              color: '#F0F0F0',
              marginBottom: '0'
            }}
          >
            <TableContainer className="device-table">
              <Table
                sx={{ minWidth: 750, color: '#f0f0f0' }}
                aria-labelledby="tableTitle"
                size={'small'}
              >
                <EnhancedTableHead
                  rowCount={batteryDataList.length}
                  dataList={batteryDataList}
                  order={tableParams.sort.order}
                  orderBy={tableParams.sort.orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {batteryDataList.length > 0 &&
                    batteryDataList.map((row: ZBatteryData, index: number) => {
                      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions

                      return (
                        <StyledTableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.kpi_name}
                          aria-label="kpis-list-row"
                        >
                          <StyledTableCell>
                            {row.kpi_name}
                          </StyledTableCell>
                          <StyledTableCell>
                            {parseDateInAOHstandard(row.timestamp)}
                          </StyledTableCell>
                          <StyledTableCell>
                            {row.kpi_value}
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            {props.batteryData.data.total_count > 10 && (<Pagination count={props.batteryData.data.total_count} page={tableParams.page} setPage={(val: number) => { dispatchTableParams({ page: val }) }} rowsPerPage={tableParams.rowsPerPage} siblingCount={1}
              boundaryCount={1} />)}
          </Paper>
        </>
      )
    } else if (props.batteryData.isLoading) {
      return (
        <div className='CircularprogressAmi'>
          <CircularProgress />
        </div>
      )
    } else if (props.batteryData.httpStatus === 401) {
      return (
        <div className='authError'><AuthError errorMessage="Unauthorized" /></div>
      )
    } else if (props.batteryData.httpStatus === 403) {
      return (
        <div className='authError'><AuthError errorMessage="accessForbidden" /></div>
      )
    } else if (props.batteryData.httpStatus === 404) {
      return (
        <div className='authError'><AuthError errorMessage="NoDataPresent" /></div>
      )
    } else if (((props.batteryData.data === null || props.batteryData.data.kpi_values.length === 0) && props.batteryData.httpStatus === 200) || (props.batteryData.httpStatus === 404)) {
      return (
        <div className='authError'><AuthError errorMessage="NoDataPresent" /></div>
      )
    } else if (props.batteryData.httpStatus === 400) {
      return <div className="authError"><AuthError errorMessage="invalidSearchInput" /></div>
    } else {
      return (
        <div className='authError'><AuthError errorMessage="cannotFetch" retry={handleFetchBatteryDataList} /></div>
      )
    }
  }

  return (
    <>
          <Paper
        className="searchDiagnosticBoxComponent"
        sx={{
          p: '8px 2px',
          display: 'flex',
          alignItems: 'center',
          height: '8vh'
        }}
      >
        <span
          style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}
        >
          <span
            style={{ paddingRight: '6px', fontSize: '14px', color: '#F0F0F0', marginLeft: '1em' }}
          >
            {DatePickerInfoStrings.DropdownLabel}
          </span>
          <div style={{ minWidth: '250px' }}>
            <GenXDropdown
              options={kpiOptions}
              value={tableParams.selectedKpi}
              onSelect={handleKpiChange}
              placeholder="Select KPI"
              width="250px"
            />
          </div>
        </span>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            backgroundColor: '#606060',
            marginX: '1rem',
            height: 'auto',
            width: '1px'
          }}
        />
        <span
          style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}
        >
          <span
            style={{
              paddingRight: '6px',
              fontSize: '14px',
              color: '#F0F0F0'
            }}
          >
            {DatePickerInfoStrings.StartDate}
          </span>
          <span className="datePicker-deviceDetails">
            <CustomLocalizationProviderWrapper>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <WhiteDatePicker
                  minDate={tableParams.end.clone().subtract(3, 'month')}
                  disableFuture
                  closeOnSelect={false}
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['cancel', 'accept'] },
                    field: { readOnly: true }
                  }}
                  defaultValue={defaultDate.start}
                  value={tableParams.start}
                  onAccept={(val: any) => {
                    handleDateChange('start', val)
                  }}
                />
              </LocalizationProvider>
            </CustomLocalizationProviderWrapper>
          </span>
        </span>
        <span
          style={{ paddingRight: '1em', display: 'flex', alignItems: 'center' }}
        >
          <span
            style={{ paddingRight: '6px', fontSize: '14px', color: '#F0F0F0' }}
          >
            {DatePickerInfoStrings.EndDate}
          </span>
          <span className="datePicker-deviceDetails">
            <CustomLocalizationProviderWrapper>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <WhiteDatePicker
                  disableFuture
                  closeOnSelect={false}
                  ampm={false}
                  timeSteps={{ minutes: 1 }}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['cancel', 'accept'] },
                    field: { readOnly: true }
                  }}
                  defaultValue={defaultDate.end}
                  value={tableParams.end}
                  minDate={tableParams.start}
                  onAccept={(val: any) => {
                    handleDateChange('end', val)
                  }}
                />
              </LocalizationProvider>
            </CustomLocalizationProviderWrapper>
          </span>
          <span style={{ marginRight: '2.5rem', marginBottom: '1.5rem' }}>
                {getDatePickerInfo()}
          </span>
        </span>
      </Paper>
    <Card sx={{ borderRadius: '10px', backgroundColor: '#353535', marginRight: '1rem' }}>
          <CardContent sx={{ padding: '0px !important', color: '#F0F0F0 !important' }}>
      <Box sx={style} className="devices">
        {handleTable()}
      </Box>
    </CardContent>
    </Card>
    </>
  )
}

interface DispatchToProps {
  fetchBatteryData: (tenant: string, assetId: string, from: string, to: string, page: number, size: number, kpiId: string, sortField: string, sortDir: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchBatteryData: (tenant: string, assetId: string, from: string, to: string, page: number, size: number, kpiId: string, sortField: string, sortDir: string) => dispatch(fetchBatteryData(tenant, assetId, from, to, page, size, kpiId, sortField, sortDir))
})

interface StateToProps {
  batteryData: batteryState
  diagnosticKpiList: any // We'll type this properly once the store is configured
}

const mapStateToProps = (state: RootState): StateToProps => ({
  batteryData: state.batteryData,
  diagnosticKpiList: (state as any).diagnosticKpiList || { kpiData: null, isLoading: false, errorMessage: null }
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(DiagnosticTable)
